// The seam between an authored score and a playable arrangement.
//
// A Feel cue (#173) says "Latin from here" — a musical direction with no
// playback identity. An Arrangement (#64) says which groove state is in force
// over which performed span. This module is the compile step between them,
// and it is where the layering is actually enforced: the score contributes
// WHERE and WHAT-IS-MEANT, the arrangement contributes HOW IT SOUNDS, and
// neither can reach into the other.
//
// Keeping it pure and separate is what makes the same written score playable
// two ways — a Latin/Swing reading and an all-swing reading are two
// assignments over one unchanged set of cues.

import type { FeelCue } from '@/music/score/types';
import type { Arrangement, GrooveChange, GroovePatch, GrooveState } from './types';

/** The measure fields the compile step reads; both chart and form measures fit. */
export interface CuedMeasure {
  id: string;
  feel?: FeelCue;
}

export interface CueCompileOptions {
  /** Identity of the produced arrangement. */
  id?: string;
  name?: string;
  /** The state the performance opens in. */
  base: GrooveState;
  /**
   * The tune's Feel → groove assignment. Returning undefined means "no groove
   * assigned to this Feel", which is reported rather than guessed: inventing
   * a groove for an unassigned Feel would make a missing decision inaudible.
   */
  grooveFor: (feel: string) => GrooveState | undefined;
}

export interface CompiledCues {
  arrangement: Arrangement;
  /** Feels the score asks for that the arrangement has no groove for. */
  unassignedFeels: string[];
  /** Distinct Feel labels the score contains, in first-appearance order. */
  feels: string[];
}

/**
 * Compile authored cues into an Arrangement the resolver can expand.
 *
 * Cues anchor to the AUTHORED measure id, exactly as `GrooveChange` requires,
 * so expansion happens later against the performed order: a cue in a first
 * ending fires only on the pass that plays it, and returning to the top of
 * the form re-fires the cue there. That is why this step does not need to
 * know anything about repeats.
 */
export function compileCues(
  measures: readonly CuedMeasure[],
  options: CueCompileOptions,
): CompiledCues {
  const changes: GrooveChange[] = [];
  const feels: string[] = [];
  const unassigned: string[] = [];

  measures.forEach((measure, index) => {
    const cue = measure.feel;
    const label = cue?.label?.trim();
    if (!cue || !label) return;
    if (!feels.includes(label)) feels.push(label);

    const state = options.grooveFor(label);
    if (!state) {
      if (!unassigned.includes(label)) unassigned.push(label);
      return;
    }
    changes.push({
      // Deterministic: the same score compiles to the same change ids, so a
      // per-cue override keyed by id survives a reload.
      id: `cue:${measure.id}:${index}`,
      at: { measureId: measure.id, ...(cue.offset ? { offset: cue.offset } : {}) },
      patch: stateToPatch(state),
      origin: 'authored',
    });
  });

  return {
    arrangement: {
      id: options.id ?? 'arrangement:cues',
      name: options.name ?? 'Score feels',
      base: options.base,
      changes,
    },
    unassignedFeels: unassigned,
    feels,
  };
}

/**
 * A whole groove state as a patch.
 *
 * A Feel cue means "play it this way now", not "adjust one channel", so every
 * field is set. Partial patches are for a future per-channel cue; leaving a
 * channel out here would let the previous Feel's bass leak into the new one.
 */
function stateToPatch(state: GrooveState): GroovePatch {
  return {
    feel: { ...state.feel },
    lead: state.lead,
    comp: state.comp,
    bass: state.bass,
    drums: state.drums,
  };
}

/** Distinct Feel labels a score asks for, in first-appearance order. */
export function feelsInScore(measures: readonly CuedMeasure[]): string[] {
  const out: string[] = [];
  for (const measure of measures) {
    const label = measure.feel?.label?.trim();
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}
